import {
  SalesforceActions,
  SalesforceAutomation,
} from 'module/api/integrations/salesforce/types';
import { Breadcrumb } from 'module/ui/components/atoms/breadcrumb';
import { ButtonGroup } from 'module/ui/components/atoms/button';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { Page } from 'module/ui/components/molecules/page';
import { showToast, ToastType } from 'module/ui/components/molecules/toasts';
import { Argon } from 'module/ui/components/templates/argon';
import { Button, Loader } from '@rocketlane/ui';
import { useUpdateAutomationMutation } from 'queries/salesforce';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { transformApiToSfFormat } from 'components/organisms/integrations/salesforce/utils/transformApiToSfFormat';
import { transformSfToApiFormat } from 'components/organisms/integrations/salesforce/utils/transformSfToApiFormat';
import { getSalesforceAutomationUrl } from 'components/pages/integrations/salesforce/utils/urls';
import { EditAutomation } from './edit-automation';
import { useAutomationContext } from '../create/automation-context';

const {
  Header: { Title },
} = Page;
const PageActionWrapper = styled(CenterAlignedBox)``;

const requiredFields = [
  'automationName',
  'salesforceObject',
  'triggers',
  'accountFields',
];

interface EditAutomationLayoutProps {
  initialData: SalesforceAutomation;
}

function EditAutomationLayout({ initialData }: EditAutomationLayoutProps) {
  const {
    automationDetails,
    setAutomationDetails,
    setIsContactSelected,
    isContactSelected,
    isContactSyncConfigured,
  } = useAutomationContext();

  // Initialize automation details from API data
  useEffect(() => {
    if (initialData) {
      const transformedData = transformApiToSfFormat(initialData);

      setAutomationDetails(transformedData);

      // Initialize isContactSelected based on whether contact data exists
      if (
        transformedData.contactFields &&
        Object.keys(transformedData.contactFields).length > 0
      ) {
        setIsContactSelected(true);
      }
    }
  }, [initialData, setAutomationDetails, setIsContactSelected]);
  const { integrationId, automationId } = useParams<{
    integrationId: string;
    automationId: string;
  }>();
  const { mutate: updateAutomationMutation, status: updateStatus } =
    useUpdateAutomationMutation();
  const navigate = useNavigate();
  const [isSaveButtonLoading, setIsSaveButtonLoading] = useState(false);

  const canEnableSaveButton = useMemo(() => {
    const clonedRequiredFields = [...requiredFields];

    if (automationDetails?.salesForceType === SalesforceActions.Project) {
      clonedRequiredFields.push('rocketlaneFields');
    }

    const isValid = clonedRequiredFields.every(
      (field) => automationDetails?.[field],
    );

    // If contact is selected, check if it's properly configured
    if (isContactSelected) {
      const hasContactObject = automationDetails?.contactFields?.contactObject;
      const isContactValid = hasContactObject && isContactSyncConfigured;

      return isValid && isContactValid;
    }

    return isValid;
  }, [automationDetails, isContactSelected, isContactSyncConfigured]);

  const handleSave = useCallback(() => {
    if (automationDetails && integrationId && automationId) {
      setIsSaveButtonLoading(true);
      const payload = transformSfToApiFormat(
        parseInt(integrationId),
        automationDetails,
      );

      if (payload) {
        updateAutomationMutation(
          { ...payload, automationId: parseInt(automationId) },
          {
            onSuccess: () => {
              navigate(getSalesforceAutomationUrl(integrationId), {
                replace: true,
              });
            },
            onError: (error) => {
              setIsSaveButtonLoading(false);
              showToast({
                toastContainer: { current: document.body },
                content: error.message,
                variant: ToastType.Error,
              });
            },
          },
        );
      } else {
        setIsSaveButtonLoading(false);
      }
    }
  }, [
    automationDetails,
    integrationId,
    automationId,
    updateAutomationMutation,
    navigate,
  ]);

  const handleCancel = useCallback(() => {
    navigate(getSalesforceAutomationUrl(integrationId || ''), {
      replace: true,
    });
  }, [navigate, integrationId]);

  return (
    <Argon
      breadcrumbs={
        <Breadcrumb.Item>
          <Link to={getSalesforceAutomationUrl(integrationId || '')}>
            <FormattedMessage id="salesforce" />
          </Link>
          <Breadcrumb.Separator />
        </Breadcrumb.Item>
      }
      pageActions={
        <PageActionWrapper>
          <ButtonGroup $reversed>
            <Button
              variant="primary"
              disabled={!canEnableSaveButton || isSaveButtonLoading}
              onClick={handleSave}
              loading={isSaveButtonLoading || updateStatus === 'pending'}
            >
              {isSaveButtonLoading || updateStatus === 'pending' ? (
                <Loader />
              ) : (
                <FormattedMessage id="save" />
              )}
            </Button>
            <Button variant="secondary" onClick={handleCancel}>
              <FormattedMessage id="cancel" />
            </Button>
          </ButtonGroup>
        </PageActionWrapper>
      }
      title={
        <Title>{automationDetails?.automationName || 'Edit automation'}</Title>
      }
      documentTitle={automationDetails?.automationName || 'Edit automation'}
      actions={{}}
      content={
        <>
          <EditAutomation />
        </>
      }
    />
  );
}

export { EditAutomationLayout };
